import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, GitBranch, Sparkles } from "lucide-react";
import { api } from "../utils/api";
import { useAuth } from "../contexts/AuthContext";
import ActionBar from "../components/ActionBar";
import AttachmentList from "../components/AttachmentList";
import AttachmentUpload from "../components/AttachmentUpload";
import CommentThread from "../components/CommentThread";
import FeedbackWidget from "../components/FeedbackWidget";
import ClosureHistory from "../components/ClosureHistory";
import ResolutionDialog from "../components/ResolutionDialog";
import AssignDialog from "../components/AssignDialog";

const WORKFLOW_LOCKED = ["approved", "rejected", "it_security_review", "it_manager_review"];
// v1.9: completed/closed/canceled/reopened go through the modal
const SELECTABLE = ["pending", "assigned", "in_progress", "pending_user_info", "pending_vendor_info"];
const CLOSURE_DIALOG_CONFIG = {
  completed: { title: "Complete service request", actionLabel: "Mark Completed", fieldLabel: "Fulfilment notes", tone: "primary", required: true },
  closed:    { title: "Close service request",    actionLabel: "Close & Archive", fieldLabel: "Closure notes", tone: "primary", required: true },
  canceled:  { title: "Cancel service request",   actionLabel: "Cancel request",  fieldLabel: "Reason for cancellation", tone: "danger", required: true },
  reopened:  { title: "Reopen service request",   actionLabel: "Reopen",          fieldLabel: "Reason for reopening", tone: "primary", required: true },
};

export default function ServiceRequestDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, hasRole } = useAuth();
  const [sr, setSr] = useState(null);
  const [users, setUsers] = useState([]);
  const [notes, setNotes] = useState("");
  const [newAttachments, setNewAttachments] = useState([]);
  const [dialogAction, setDialogAction] = useState(null);
  // v2.2 #1: in-app Assign modal
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignSuggestion, setAssignSuggestion] = useState(null);

  async function load() {
    const { data } = await api.get(`/api/service-requests/${id}`); setSr(data);
    setNotes(data.approval_notes || "");
  }
  useEffect(() => {
    load();
    if (hasRole("admin", "it_team", "it_manager", "it_security")) api.get("/api/users").then((r) => setUsers(r.data));
  }, [id]); // eslint-disable-line

  async function update(payload) {
    try {
      const { data } = await api.put(`/api/service-requests/${id}`, payload);
      setSr(data);
      return data;
    } catch (e) {
      alert(e.response?.data?.detail || "Update failed");
      throw e;
    }
  }

  async function uploadAttachments() {
    if (!newAttachments.length) return;
    await update({ attachments: newAttachments });
    setNewAttachments([]);
  }

  async function openAssignDialog() {
    try {
      const { data } = await api.get(`/api/users/suggest-assignee?ticket_type=service_request&category=${encodeURIComponent(sr.category)}`);
      if (data.assignee) {
        setAssignSuggestion({ ...data.assignee, open_count: data.open_count, category_familiarity: data.category_familiarity });
      } else {
        setAssignSuggestion(null);
      }
    } catch {
      setAssignSuggestion(null);
    }
    setAssignOpen(true);
  }
  async function handleAssignSubmit(userId) {
    await update({ assigned_to_id: userId });
    setAssignOpen(false);
  }

  async function convertToProblem() {
    if (!window.confirm(`Create a Problem record from service request ${sr.request_number}?`)) return;
    try {
      const { data } = await api.post(`/api/service-requests/${id}/convert-to-problem`);
      if (window.confirm(`Created ${data.problem_number}. Open it now?`)) nav(`/problems/${data.problem_id}`);
    } catch (e) { alert(e.response?.data?.detail || "Conversion failed"); }
  }

  async function handleDialogSubmit(text) {
    if (!dialogAction) return;
    await update({ status: dialogAction, fulfillment_notes: text });
    setDialogAction(null);
  }

  if (!sr) return <div>Loading…</div>;

  // v2.8: admin can approve as any role
  const canApproveSec = hasRole("admin", "it_security") && sr.status === "it_security_review";
  const canApproveMgr = hasRole("admin", "it_manager") && sr.status === "it_manager_review";
  const isIT = hasRole("admin", "it_team", "it_manager");

  // Action verbs
  const actions = [];
  if (canApproveSec || canApproveMgr) {
    actions.push({ key: "approve", label: "Approve", primary: true, onClick: () =>
      update(canApproveSec ? { it_security_approved: true, approval_notes: notes } : { it_manager_approved: true, approval_notes: notes }) });
    actions.push({ key: "reject", label: "Reject", danger: true, onClick: () =>
      update(canApproveSec ? { it_security_approved: false, approval_notes: notes } : { it_manager_approved: false, approval_notes: notes }) });
  } else if (isIT) {
    actions.push({ key: "assign", label: "Assign", primary: true, onClick: openAssignDialog });
    if (["completed", "closed"].includes(sr.status)) {
      actions.push({ key: "reopen", label: "Reopen", onClick: () => setDialogAction("reopened") });
    } else if (!WORKFLOW_LOCKED.includes(sr.status)) {
      if (sr.status !== "in_progress")
        actions.push({ key: "modify", label: "Mark In-Progress", onClick: () => update({ status: "in_progress" }) });
      actions.push({ key: "complete", label: "Complete", primary: true, onClick: () => setDialogAction("completed") });
      actions.push({ key: "close", label: "Close", onClick: () => setDialogAction("closed") });
      actions.push({ key: "cancel", label: "Cancel", danger: true, onClick: () => setDialogAction("canceled") });
    }
    actions.push({ key: "convert", label: "Create Problem", icon: GitBranch, onClick: convertToProblem });
  }

  const dlg = dialogAction ? CLOSURE_DIALOG_CONFIG[dialogAction] : null;
  // v2.0 #8
  const isClosedSr = ["completed", "closed", "canceled"].includes(sr.status);

  return (
    <div className="space-y-4">
      <Link to="/service-requests" className="text-sm text-brand-600 hover:underline flex items-center gap-1"><ArrowLeft size={14} /> Back</Link>
      <div className="card p-5">
        <div className="flex justify-between">
          <div>
            <div className="text-xs font-mono text-gray-500">{sr.request_number}</div>
            <h1 className="text-xl font-bold">{sr.title}</h1>
            <div className="text-sm text-gray-600">Requester: {sr.requester_name} · Type: <b>{sr.request_type}</b> · Plant: {sr.plant}</div>
          </div>
          <span className="badge bg-brand-50 text-brand-700">{sr.status}</span>
        </div>
        <p className="mt-4 text-sm whitespace-pre-wrap">{sr.description}</p>
      </div>

      {actions.length > 0 && <ActionBar actions={actions} />}

      {(canApproveSec || canApproveMgr) && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold">{canApproveSec ? "IT Security Approval" : "IT Manager Approval"}</h2>
          <textarea className="input" rows="2" placeholder="Approval notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      )}

      {isIT && (
        <div className="card p-5 space-y-3">
          <h2 className="font-semibold">Fulfilment</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label flex items-center justify-between">
                <span>Assignee</span>
                {!isClosedSr && (
                  <button type="button" onClick={openAssignDialog} className="text-xs text-brand-600 hover:underline flex items-center gap-1">
                    <Sparkles size={12} /> Suggest
                  </button>
                )}
              </label>
              {isClosedSr ? (
                <div className="input bg-slate-50 text-slate-600 cursor-not-allowed">
                  {sr.assigned_to_name || "— Unassigned —"}
                  <span className="ml-2 text-xs text-slate-500">(reopen to reassign)</span>
                </div>
              ) : (
                <select className="input" value={sr.assigned_to_id || ""}
                  onChange={(e) => update({ assigned_to_id: e.target.value || null })}>
                  <option value="">— Unassigned —</option>
                  {users.filter((u) => ["it_team", "it_manager", "it_security"].includes(u.role))
                    .map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
                </select>
              )}
            </div>
            <div>
              <label className="label">Status (raw)</label>
              <select className="input" value={SELECTABLE.includes(sr.status) ? sr.status : ""}
                onChange={(e) => { if (e.target.value) update({ status: e.target.value }); }}>
                {!SELECTABLE.includes(sr.status) && <option value="">{sr.status} (workflow/closure)</option>}
                {SELECTABLE.map((s) => <option key={s}>{s}</option>)}
              </select>
              <p className="text-xs text-slate-500 mt-1">
                Complete / Close / Cancel / Reopen require notes. Use the action buttons above — notes are captured in the permanent resolution history.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="card p-5">
        <h2 className="font-semibold mb-3">Approval state</h2>
        <ul className="text-sm space-y-1">
          <li>IT Security: <b>{sr.it_security_approved === true ? "Approved" : sr.it_security_approved === false ? "Rejected" : "Pending"}</b></li>
          <li>IT Manager: <b>{sr.it_manager_approved === true ? "Approved" : sr.it_manager_approved === false ? "Rejected" : "Pending"}</b></li>
        </ul>
      </div>

      {isIT && (
        <div className="card p-5 space-y-2">
          <h2 className="font-semibold">Add attachments</h2>
          <AttachmentUpload value={newAttachments} onChange={setNewAttachments} />
          {newAttachments.length > 0 && (
            <button className="btn-primary" onClick={uploadAttachments}>Upload {newAttachments.length} file(s)</button>
          )}
        </div>
      )}

      <AttachmentList items={sr.attachments} />

      <ClosureHistory entries={sr.closure_history || []} title="Fulfilment / Closure history" />

      <CommentThread kind="service-requests" ticket={sr} onPosted={load} />

      <FeedbackWidget kind="service-requests" ticket={sr} currentUser={user} onSubmitted={() => load()} />

      {dlg && (
        <ResolutionDialog
          open={!!dialogAction}
          onClose={() => setDialogAction(null)}
          onSubmit={handleDialogSubmit}
          title={dlg.title}
          fieldLabel={dlg.fieldLabel}
          actionLabel={dlg.actionLabel}
          tone={dlg.tone}
          required={dlg.required}
        />
      )}

      <AssignDialog
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        onSubmit={handleAssignSubmit}
        suggested={assignSuggestion}
        users={users}
        allowedRoles={["it_team", "it_manager", "it_security"]}
        ticketCategory={sr.category}
        ticketNumber={sr.request_number}
      />
    </div>
  );
}
